//import Vapi from "./vapi";
import Vapi from "@vapi-ai/web";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import en from "src/localization/english/en";
import { getAccessToken } from "src/utils/authentication.service";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;

const VapiContext = React.createContext<any>(null);

const getAvailableDevices = async () => {
  const all = await navigator.mediaDevices.enumerateDevices();
  return {
    audioInputs: all.filter(d => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== "communications"),
    audioOutputs: all.filter(d => d.kind === "audiooutput" && d.deviceId !== "default" && d.deviceId !== "communications"),
    videoInputs: all.filter(d => d.kind === "videoinput" && d.deviceId !== "default"),
  };
};

interface VapiContextProviderProps {
  children: React.ReactNode;
}

export const VapiContextProvider: React.FC<VapiContextProviderProps> = ({ children }) => {
  const context = useState({
    vapiClient: null,
    started: false,
    ended: false,
    transcripts: [],
    videoReady: false,   
    isVideoCall: false, 
  });
  return (
    <VapiContext.Provider value={context}>
      {children}
    </VapiContext.Provider>
  );
};

export const useVapi = () => {
  const [context, setContext] = useContext(VapiContext);

  useEffect(() => {
    if(context.vapiClient) {
      return;
    }

    //const vapiClient = new Vapi();
    const vapiClient = new Vapi(getAccessToken(), `${apiUrl}/api/1.0/simulations/vapi`);
    //const vapiClient = new Vapi("4ae9f112-d64f-4788-ae5d-ef2f1eb38ac7");
    vapiClient?.on("message", handleMessageChange);

    vapiClient?.on("call-start", handleCallStart);

    vapiClient?.on("call-end", handleCallEnd);

    vapiClient?.on("volume-level", handleVolumeChange);

    setContext((prev) => {
      return {
        ...prev,
        vapiClient: vapiClient,
        transcripts: [],
      }
    });

    return () => {
      if (vapiClient) {
        vapiClient?.off("message", handleMessageChange);
        vapiClient?.off("call-start", handleCallStart);
        vapiClient?.off("call-end", handleCallEnd);
        vapiClient?.off("volume-level", handleVolumeChange);
      }
    }
  }, []);
  
  const startCall = async (
    sessionId: string,
    isVideo = false,
    selectedDevices?: any,
    onDeviceChange?: (type: string, id: string) => void,
  ) => {
    try {
      if (!context.vapiClient) return;

      if (isVideo) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: selectedDevices?.videoInput
              ? { exact: selectedDevices.videoInput }
              : undefined,
              width: { ideal: 3840 },     
            height: { ideal: 2160 },    
            frameRate: { ideal: 60 }, 
            facingMode: 'user',
            aspectRatio: { ideal: 16/9 },
          }
        });
        setContext((prev: any) => ({ ...prev, videoStream: stream, isVideoCall: true }));
      }

      setContext((prev: any) => ({
        ...prev,
        pendingSelectedDevices: selectedDevices,
        onDeviceChange,
      }));

      await context.vapiClient.start(sessionId);
    } catch (err) {
      console.error("Failed to start call:", err);
    }
  };

  const handleCallStart = () => {
    setContext((prev: any) => {
      if (prev.isVideoCall && prev.vapiClient) {
        prev.vapiClient.setMuted(true);
      }

      const dailyCall = (prev.vapiClient as any)?.call;

      if (dailyCall && prev.pendingSelectedDevices) {
        const { audioInput, audioOutput, videoInput } = prev.pendingSelectedDevices;
        dailyCall.setInputDevicesAsync({
          audioDeviceId: audioInput || undefined,
          videoDeviceId: videoInput || undefined,
        }).catch(console.error);
        if (audioOutput) {
          dailyCall.setOutputDeviceAsync({ outputDeviceId: audioOutput }).catch(console.error);
        }
      }
      const handleDeviceChange = async () => {
        if (!dailyCall) return;
        try {
          const { audioInputs, audioOutputs, videoInputs } = await getAvailableDevices();

          const currentInputs = await dailyCall.getInputDevices();
          const currentMicId = currentInputs?.microphone?.deviceId;
          const currentCamId = currentInputs?.camera?.deviceId;
          const currentOutputId = (await dailyCall.getOutputDevice?.())?.outputDeviceId;

          const currentMicStillExists = audioInputs.some(d => d.deviceId === currentMicId);
          const currentOutputStillExists = audioOutputs.some(d => d.deviceId === currentOutputId);
          const currentCamStillExists = videoInputs.some(d => d.deviceId === currentCamId);

          if (!currentMicStillExists) {
            const all = await navigator.mediaDevices.enumerateDevices();
            const systemDefault = all.find(d => d.kind === "audioinput" && d.deviceId === "default");
            const fallback =
              audioInputs.find(d => !!systemDefault?.groupId && d.groupId === systemDefault.groupId) ??
              audioInputs[0];
            if (fallback) {
              await dailyCall.setInputDevicesAsync({ audioDeviceId: fallback.deviceId });
              prev.onDeviceChange?.("audioInput", fallback.deviceId);
            }
          }

          if (!currentOutputStillExists) {
            const all = await navigator.mediaDevices.enumerateDevices();
            const systemDefault = all.find(d => d.kind === "audiooutput" && d.deviceId === "default");
            const fallback =
              audioOutputs.find(d => !!systemDefault?.groupId && d.groupId === systemDefault.groupId) ??
              audioOutputs[0];
            if (fallback) {
              await dailyCall.setOutputDeviceAsync({ outputDeviceId: fallback.deviceId });
              prev.onDeviceChange?.("audioOutput", fallback.deviceId);
            }
          }

          if (prev.isVideoCall && !currentCamStillExists) {
            const fallback = videoInputs[0];
            if (fallback) {
              await dailyCall.setInputDevicesAsync({ videoDeviceId: fallback.deviceId });
              prev.onDeviceChange?.("videoInput", fallback.deviceId);
          
              const newStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: fallback.deviceId } }
              });
              setContext((c: any) => ({ ...c, videoStream: newStream }));
            }
          }

        } catch (err) {
          console.error("Device change handling failed:", err);
        }
      };

      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

      return {
        ...prev,
        started: true,
        ended: false,
        videoReady: !prev.isVideoCall,
        pendingSelectedDevices: null,
        deviceChangeCleanup: () =>
          navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange),
      };
    });
  };

  const handleCallEnd = () => {
    setContext((prev: any) => {
      const dailyCall = (prev.vapiClient as any)?.call;
      prev.videoStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      if (dailyCall) {
        try { dailyCall.setLocalAudio(false, { forceDiscardTrack: true }); } catch {}
        try { dailyCall.setLocalVideo(false); } catch {}
        try { dailyCall.leave(); } catch {}
      }
      prev.deviceChangeCleanup?.();
      return {
        ...prev,
        started: false,
        ended: true,
        videoStream: null,
        deviceChangeCleanup: null,
      };
    });
  };

  const handleVolumeChange = (volume: number) => {
    setContext((prev: any) => {
      return {
        ...prev,
        volume: volume,
      }
    });
  }

  const handleMessageChange = (message: any) => {
    if (message.type === 'conversation-update') {
      const transcripts = (message.conversation || []).filter((conversation: any) => {
        return _.includes(['assistant', 'user'], conversation.role);
      });

      setContext((prev: any) => {
        return {
          ...prev,
          transcripts: transcripts
        }
      });
    }
  }

  const setVideoReady = (ready: boolean) => {
    setContext((prev: any) => {
      if (ready) prev.vapiClient?.setMuted(false);
      return { ...prev, videoReady: ready };
    });
  };

  return { ...context, startCall, setVideoReady };
}